/**
 * 메인 페이지 레이아웃 컴포넌트
 *
 * - 사이드바 없음 (추후 필요 시 추가)
 * - AppHeader + 콘텐츠 + AppFooter 단순 구조
 * - 홈: 전체 너비, 흰 배경, HomeNavBar 표시
 * - 기능 페이지: 회색 배경 + 흰 카드
 */
import React, { useEffect, useState } from 'react';
import { Layout, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import useResponsive from '../../hooks/useResponsive';
import AppHeader from './AppHeader';
import HomeNavBar from './HomeNavBar';
import AppFooter from './AppFooter';
import LoginModal from './LoginModal';
import MobileBottomNav from './MobileBottomNav';
import AppBreadcrumb from '../common/AppBreadcrumb';
import useTheme from '../../hooks/useTheme';

const { Content } = Layout;

/* ── 페이지 트랜지션 CSS ── */
const transitionStyle = document.createElement('style');
transitionStyle.textContent = `
  @keyframes pageFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .page-transition-wrapper {
    animation: pageFadeIn 0.25s ease-out;
  }
`;
if (!document.querySelector('[data-page-transition]')) {
  transitionStyle.setAttribute('data-page-transition', 'true');
  document.head.appendChild(transitionStyle);
}

// 자체 카드 레이아웃을 사용하는 페이지 (회색 배경, 흰 카드 래퍼 없음)
// Hero 디자인을 쓰는 페이지들은 이 목록에 등록해야 PageLayout이 흰 카드로 한 번 더 감싸는 것을 방지한다.
//
// 디자인 시스템 통합 Phase 5: 모든 새 Hero 패턴 페이지 등록
const SELF_LAYOUT_PATHS = [
  // PRD
  '/prd/pesticides', '/prd/mrls', '/prd/foods', '/prd/adi', '/prd/progress',
  '/prd/analysis', '/prd/analysis-l', '/prd/reports', '/prd/using',
  // VD
  '/vd/drugs', '/vd/foods', '/vd/mrls', '/vd/adi',
  '/vd/analysis', '/vd/using',
  // STD
  '/std/standards', '/std/applications', '/std/requests',
  // DOC
  '/doc/prd-board', '/doc/vd-board',
  // Proposal — 실제 라우트는 /prd/proposals (App.js)
  '/prd/proposals',
  // Admin
  '/admin/dashboard', '/admin/users', '/admin/auth-templates',
  '/admin/audit', '/admin/workflow', '/admin/batch',
  '/admin/notifications', '/admin/popups', '/admin/quality', '/admin/visitors',
  '/admin/change-password',
  // Auth (본인 비밀번호 변경)
  '/auth/change-password',
  // 기타
  '/search',
];

// prefix 기반 매칭 — /admin/master/* 하위 모든 경로를 포함한다.
const SELF_LAYOUT_PREFIXES = ['/admin/master/'];

const PageLayout = ({ children }) => {
  const { isMobile } = useResponsive();
  const { isDark } = useTheme();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isSelfLayout = SELF_LAYOUT_PATHS.includes(location.pathname)
    || SELF_LAYOUT_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  // ── 페이지 전환 오버레이 (스피너) + 푸터 제어 ──
  const [pageReady, setPageReady] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(true);

  useEffect(() => {
    setPageReady(false);
    setOverlayVisible(true);

    if (isHome) {
      // 홈: homePageLoaded 이벤트 대기
      const handler = () => {
        setPageReady(true);
        setTimeout(() => setOverlayVisible(false), 300);
      };
      window.addEventListener('homePageLoaded', handler);
      return () => window.removeEventListener('homePageLoaded', handler);
    } else {
      // 기능 페이지: 50ms 후 표시 (CSS 애니메이션 시작점 확보)
      const timer = setTimeout(() => {
        setPageReady(true);
        setTimeout(() => setOverlayVisible(false), 300);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 라우트 변경 시 Ant Design 스크롤 잠금 잔류 효과 정리 ──
  useEffect(() => {
    return () => {
      document.body.classList.remove('ant-scrolling-effect');
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
  }, [location.pathname]);

  // ── Ant Design rc-trigger 팝업 오버레이 잔류 감지 및 정리 ──
  // Dropdown hover 트리거 오류 등으로 투명 오버레이가 남아 클릭을 막는 경우 정리
  useEffect(() => {
    const cleanup = () => {
      // rc-trigger가 body에 직접 추가한 팝업 컨테이너 정리
      const staleContainers = document.querySelectorAll(
        'div[class*="ant-dropdown-hidden"], div[class*="trigger-popup-hidden"]'
      );
      staleContainers.forEach((el) => el.remove());
      // 잠긴 스크롤 해제
      if (document.body.classList.contains('ant-scrolling-effect')) {
        document.body.classList.remove('ant-scrolling-effect');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    };
    // 라우트 이동 시 즉시 정리
    cleanup();
  }, [location.pathname]);

  const spinnerIcon = <LoadingOutlined style={{ fontSize: 43, color: isDark ? '#4096ff' : '#003a8c' }} spin />;

  return (
    <>
      {/* ── 페이지 전환 오버레이 (스피너) ── */}
      {overlayVisible && !isHome && (
        <div style={{
          position: 'fixed', inset: 0,
          background: isDark ? '#141414' : '#ffffff',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 12, zIndex: 9999,
          opacity: pageReady ? 0 : 1,
          transition: 'opacity 0.25s ease',
          pointerEvents: pageReady ? 'none' : 'all',
        }}>
          <Spin indicator={spinnerIcon} />
          <span style={{ color: isDark ? '#4096ff' : '#003a8c', fontSize: 17, fontWeight: 700 }}>{(localStorage.getItem('buj-lang') === 'en') ? 'Loading...' : '로딩 중입니다.'}</span>
        </div>
      )}

      {/* ── 고정 헤더 + 네비게이션 바 (전 페이지 공통) ── */}
      <AppHeader showSidebarToggle={false} />
      <HomeNavBar />

      {/* 콘텐츠 영역 — AppHeader(64) + HomeNavBar(46) = 110px 상단 여백 */}
      {isHome ? (
        /* ── 홈: 전체 너비 흰 배경 ── */
        <Layout style={{ background: isDark ? '#141414' : '#fff', minHeight: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 110px)', marginTop: isMobile ? 64 : 110 }}>
          <Content style={{ margin: 0 }}>
            {children}
          </Content>
          <div style={{
            visibility: pageReady ? 'visible' : 'hidden',
            opacity: pageReady ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}>
            <AppFooter />
          </div>
        </Layout>
      ) : isSelfLayout ? (
        /* ── 자체 레이아웃 페이지: 회색 배경, 래퍼 없음 ── */
        <Layout
          style={{
            background: isDark ? '#141414' : '#f0f2f5',
            minHeight: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 110px)',
            marginTop: isMobile ? 64 : 110,
          }}
        >
          <Content>
            <div className="page-transition-wrapper" key={location.pathname}>
              {children}
            </div>
          </Content>
          <div style={{
            visibility: pageReady ? 'visible' : 'hidden',
            opacity: pageReady ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}>
            <AppFooter />
          </div>
        </Layout>
      ) : (
        /* ── 기능 페이지: 회색 배경 + 중앙 정렬 흰 카드 ── */
        <Layout
          style={{
            background: isDark ? '#141414' : '#f0f2f5',
            minHeight: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 110px)',
            marginTop: isMobile ? 64 : 110,
            padding: isMobile ? '16px 8px' : '24px 0',
          }}
        >
          <Content
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              width: '100%',
              padding: '0 8px',
            }}
          >
            <div
              style={{
                background: isDark ? '#1f1f1f' : '#fff',
                padding: isMobile ? '16px' : '24px',
                borderRadius: 12,
                boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <AppBreadcrumb />
              <div className="page-transition-wrapper" key={location.pathname}>
                {children}
              </div>
            </div>
          </Content>
          <div style={{
            visibility: pageReady ? 'visible' : 'hidden',
            opacity: pageReady ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}>
            <AppFooter />
          </div>
        </Layout>
      )}

      {/* 모바일 하단 네비게이션 */}
      {isMobile && <MobileBottomNav />}

      {/* 로그인 모달 (헤더 버튼 클릭 시) */}
      <LoginModal />
    </>
  );
};

export default PageLayout;
